import * as Popover from '@radix-ui/react-popover'
import React from 'react'

import { cn } from './cn'

// DOM mirrors Semantic UI React Popup for the props the app uses.
// Styles are copied from Semantic UI 2.4.2 popup.css for `.ui.popup` and top/bottom placement.

export type PopupProps = {
  trigger: React.ReactElement<React.DOMAttributes<Element>>
  content: React.ReactNode
  disabled?: boolean
  hoverable?: boolean
  className?: string
}

const CLOSE_DELAY_MS = 70

const POPUP_STYLES = [
  // Radix Popper positions the wrapper; absolute on content collapses it to 0×0 and breaks placement.
  '[&.ui.popup]:block',
  '[&.ui.popup]:min-w-min [&.ui.popup]:z-[1900]',
  '[&.ui.popup]:[border:1px_solid_#d4d4d5] [&.ui.popup]:leading-[1.4285em] [&.ui.popup]:max-w-[250px]',
  '[&.ui.popup]:bg-[#fff] [&.ui.popup]:p-[0.833em_1em] [&.ui.popup]:font-normal [&.ui.popup]:not-italic',
  '[&.ui.popup]:text-[rgba(0,0,0,0.87)] [&.ui.popup]:rounded-[0.28571429rem]',
  '[&.ui.popup]:shadow-[0_2px_4px_0_rgba(34,36,38,0.12),0_2px_10px_0_rgba(34,36,38,0.15)]',
  '[&.ui.popup]:text-[1rem] [&.ui.popup]:m-0',
  '[&.ui.popup]:[transform:translateZ(0)] [&.ui.popup]:[backface-visibility:hidden]',
  'data-[side=top]:[&.ui.popup]:mb-[0.71428571em]',
  'data-[side=bottom]:[&.ui.popup]:mt-[0.71428571em]',
  '[&.ui.popup]:before:absolute [&.ui.popup]:before:content-[""] [&.ui.popup]:before:w-[0.71428571em]',
  '[&.ui.popup]:before:h-[0.71428571em] [&.ui.popup]:before:bg-[#fff]',
  '[&.ui.popup]:before:rotate-45 [&.ui.popup]:before:z-[2]',
  '[&.ui.popup]:before:shadow-[1px_1px_0_0_#bababc]',
  'data-[side=top]:[&.ui.popup]:before:top-auto data-[side=top]:[&.ui.popup]:before:right-auto',
  'data-[side=top]:[&.ui.popup]:before:bottom-[-0.30714286em] data-[side=top]:[&.ui.popup]:before:left-[1em]',
  'data-[side=top]:[&.ui.popup]:before:ml-0 data-[side=top]:[&.ui.popup]:before:bg-[#fff]',
  'data-[side=bottom]:[&.ui.popup]:before:top-[-0.30714286em] data-[side=bottom]:[&.ui.popup]:before:left-[1em]',
  'data-[side=bottom]:[&.ui.popup]:before:right-auto data-[side=bottom]:[&.ui.popup]:before:bottom-auto',
  'data-[side=bottom]:[&.ui.popup]:before:ml-0',
  'data-[side=bottom]:[&.ui.popup]:before:shadow-[-1px_-1px_0_0_#bababc]',
  'data-[side=bottom]:[&.ui.popup]:before:bg-[#fff]',
]

function PopupRoot({
  trigger,
  content,
  disabled,
  hoverable,
  className,
}: PopupProps) {
  const [open, setOpen] = React.useState(false)
  const closeTimer = React.useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  )

  const cancelClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current)
  }

  const scheduleClose = () => {
    cancelClose()
    closeTimer.current = setTimeout(() => setOpen(false), CLOSE_DELAY_MS)
  }

  if (disabled) {
    return trigger
  }

  const triggerElement = React.cloneElement(trigger, {
    onMouseEnter: (e: React.MouseEvent) => {
      cancelClose()
      setOpen(true)
      trigger.props.onMouseEnter?.(e)
    },
    onMouseLeave: (e: React.MouseEvent) => {
      scheduleClose()
      trigger.props.onMouseLeave?.(e)
    },
    onClick: (e: React.MouseEvent) => {
      cancelClose()
      setOpen(true)
      trigger.props.onClick?.(e)
    },
  })

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>{triggerElement}</Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          className={cn('ui popup visible', POPUP_STYLES.join(' '), className)}
          side="top"
          align="start"
          sideOffset={0}
          onOpenAutoFocus={e => e.preventDefault()}
          onMouseEnter={() => {
            if (hoverable) {
              cancelClose()
              setOpen(true)
            }
          }}
          onMouseLeave={() => {
            if (hoverable) scheduleClose()
          }}
        >
          {content}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
}

export const Popup = PopupRoot
